import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Newspaper, Plus, RefreshCw, Rss, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { EmptyState, ErrorState, LoadingState, Spinner, useToast } from '../../components/Feedback';
import { formatDateTime } from '../../lib/utils';
import type { Announcement, NewsFeed } from '../../types';

/**
 * Gestion du panneau « Actualités » de l'accueil : les messages ajoutés à la
 * main rejoignent, triés par date, ceux relus depuis les flux RSS déclarés
 * ci-dessous — voir backend/src/services/hub-news.service.ts. Un message RSS
 * peut aussi être retiré d'ici ; il réapparaîtra à la prochaine lecture du
 * flux tant qu'il y figure encore.
 */
export function AdminAnnouncementsPage() {
    return (
        <div className="space-y-8">
            <NewsFeedsSection />
            <AnnouncementsSection />
        </div>
    );
}

const COULEURS_SUGGEREES = ['#2563eb', '#0078d4', '#16a34a', '#dc2626', '#9333ea', '#ea580c'];

function NewsFeedsSection() {
    const queryClient = useQueryClient();
    const { notify } = useToast();

    const { data: feeds = [], isLoading, isError, error, refetch } = useQuery({
        queryKey: ['admin-news-feeds'],
        queryFn: () => api.get<NewsFeed[]>('/api/admin/news-feeds'),
    });

    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [color, setColor] = useState(COULEURS_SUGGEREES[0]);

    const create = useMutation({
        mutationFn: () => api.post<NewsFeed>('/api/admin/news-feeds', { name: name.trim(), url: url.trim(), color }),
        onSuccess: () => {
            setName('');
            setUrl('');
            setColor(COULEURS_SUGGEREES[0]);
            notify('Flux ajouté.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['admin-news-feeds'] });
        },
        onError: (err) => notify(err instanceof ApiError ? err.message : 'L’ajout a échoué.', 'error'),
    });

    const toggle = useMutation({
        mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
            api.put<NewsFeed>(`/api/admin/news-feeds/${id}`, { enabled }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-news-feeds'] }),
        onError: () => notify('La mise à jour a échoué.', 'error'),
    });

    const remove = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/news-feeds/${id}`),
        onSuccess: () => {
            notify('Flux retiré. Les billets déjà lus restent affichés, sans flux d’origine.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['admin-news-feeds'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
        },
        onError: () => notify('Le retrait a échoué.', 'error'),
    });

    const refresh = useMutation({
        mutationFn: () => api.post<{ lus: number; retenus: number }>('/api/admin/news-feeds/refresh'),
        onSuccess: (result) => {
            notify(
                result.lus === 0
                    ? 'Aucun billet lu : vérifiez qu’au moins un flux est actif et accessible.'
                    : `${result.lus} billet${result.lus > 1 ? 's' : ''} lu${result.lus > 1 ? 's' : ''}, ${result.retenus} nouveau${result.retenus > 1 ? 'x' : ''} ou mis à jour.`,
                result.lus === 0 ? 'error' : 'success'
            );
            void queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
        },
        onError: () => notify('La relecture a échoué.', 'error'),
    });

    return (
        <section className="space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-[var(--color-text)]">Flux RSS</h2>
                    <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                        Relus toutes les six heures, et à la demande si le délai est dépassé. Chaque
                        flux garde sa couleur dans le bandeau de l’accueil : la changer ici recolore
                        aussitôt tous les billets déjà lus depuis ce flux.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => refresh.mutate()}
                    disabled={refresh.isPending || feeds.length === 0}
                    className="btn btn-secondary shrink-0"
                    title="Relit tous les flux actifs tout de suite, sans attendre"
                >
                    {refresh.isPending ? (
                        <Spinner className="w-4 h-4" />
                    ) : (
                        <RefreshCw className="w-4 h-4" aria-hidden />
                    )}
                    Actualiser maintenant
                </button>
            </div>

            <div className="card card-padded space-y-3">
                <form
                    className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] items-end"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (name.trim().length < 2) {
                            notify('Le nom doit faire au moins 2 caractères.', 'error');
                            return;
                        }
                        create.mutate();
                    }}
                >
                    <label className="block">
                        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                            Nom
                        </span>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input"
                            placeholder="Azure"
                            maxLength={80}
                            required
                        />
                    </label>

                    <label className="block">
                        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                            URL du flux RSS
                        </span>
                        <input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            className="input"
                            type="url"
                            placeholder="https://…/feed/"
                            maxLength={2048}
                            required
                        />
                    </label>

                    <label className="block">
                        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                            Couleur
                        </span>
                        <input
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            type="color"
                            className="h-9 w-14 rounded-lg border border-[var(--color-border)] bg-transparent p-1"
                            list="couleurs-suggerees"
                        />
                        <datalist id="couleurs-suggerees">
                            {COULEURS_SUGGEREES.map((c) => (
                                <option key={c} value={c} />
                            ))}
                        </datalist>
                    </label>

                    <button type="submit" className="btn btn-primary" disabled={create.isPending}>
                        {create.isPending ? <Spinner className="w-4 h-4" /> : <Plus className="w-4 h-4" aria-hidden />}
                        Ajouter
                    </button>
                </form>
            </div>

            {isLoading ? (
                <LoadingState />
            ) : isError ? (
                <ErrorState
                    message={error instanceof Error ? error.message : 'Chargement impossible.'}
                    onRetry={() => void refetch()}
                />
            ) : feeds.length === 0 ? (
                <EmptyState
                    title="Aucun flux déclaré"
                    description="Le panneau de l’accueil n’affiche que les messages manuels tant qu’aucun flux n’est ajouté."
                />
            ) : (
                <div className="card overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                <th scope="col"></th>
                                <th scope="col">Nom</th>
                                <th scope="col">URL</th>
                                <th scope="col">Actif</th>
                                <th scope="col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {feeds.map((feed) => (
                                <tr key={feed.id}>
                                    <td className="w-8">
                                        <span
                                            className="block w-3 h-3 rounded-full"
                                            style={{ backgroundColor: feed.color }}
                                            aria-hidden
                                        />
                                    </td>
                                    <td className="text-sm font-medium text-[var(--color-text)]">{feed.name}</td>
                                    <td className="text-xs text-[var(--color-text-muted)] max-w-xs truncate">
                                        {feed.url}
                                    </td>
                                    <td>
                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={feed.enabled}
                                                onChange={(e) =>
                                                    toggle.mutate({ id: feed.id, enabled: e.target.checked })
                                                }
                                            />
                                            <span className="text-xs text-[var(--color-text-secondary)]">
                                                {feed.enabled ? 'Actif' : 'Suspendu'}
                                            </span>
                                        </label>
                                    </td>
                                    <td className="text-right">
                                        <button
                                            type="button"
                                            onClick={() => remove.mutate(feed.id)}
                                            disabled={remove.isPending}
                                            className="btn btn-ghost p-1.5 text-[var(--color-error)]"
                                            aria-label={`Retirer le flux ${feed.name}`}
                                            title="Retirer ce flux"
                                        >
                                            <Trash2 className="w-4 h-4" aria-hidden />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function AnnouncementsSection() {
    const queryClient = useQueryClient();
    const { notify } = useToast();

    const { data: announcements = [], isLoading, isError, error, refetch } = useQuery({
        queryKey: ['admin-announcements'],
        queryFn: () => api.get<Announcement[]>('/api/admin/announcements'),
    });

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [url, setUrl] = useState('');

    const create = useMutation({
        mutationFn: () =>
            api.post<Announcement>('/api/admin/announcements', {
                title: title.trim(),
                body: body.trim() || undefined,
                url: url.trim() || undefined,
            }),
        onSuccess: () => {
            setTitle('');
            setBody('');
            setUrl('');
            notify('Message ajouté à l’accueil.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
        },
        onError: () => notify('L’ajout a échoué.', 'error'),
    });

    const archive = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/announcements/${id}`),
        onSuccess: () => {
            notify('Message retiré de l’accueil.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
        },
        onError: () => notify('Le retrait a échoué.', 'error'),
    });

    return (
        <section className="space-y-4">
            <h2 className="text-lg font-semibold text-[var(--color-text)]">Messages manuels</h2>

            <div className="card card-padded space-y-3">
                <p className="text-xs text-[var(--color-text-muted)]">
                    Visible dans le panneau « Actualités » de l’accueil, aux côtés des billets des flux
                    RSS ci-dessus.
                </p>

                <form
                    className="space-y-3"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (title.trim().length < 2) {
                            notify('Le titre doit faire au moins 2 caractères.', 'error');
                            return;
                        }
                        create.mutate();
                    }}
                >
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                            <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                                Titre
                            </span>
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="input"
                                maxLength={200}
                                required
                            />
                        </label>

                        <label className="block">
                            <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                                Lien (optionnel)
                            </span>
                            <input
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                className="input"
                                type="url"
                                placeholder="https://…"
                                maxLength={2048}
                            />
                        </label>
                    </div>

                    <label className="block">
                        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                            Détail (optionnel)
                        </span>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className="input"
                            rows={2}
                            maxLength={2000}
                        />
                    </label>

                    <button type="submit" className="btn btn-primary" disabled={create.isPending}>
                        {create.isPending && <Spinner className="w-4 h-4" />}
                        Publier
                    </button>
                </form>
            </div>

            {isLoading ? (
                <LoadingState />
            ) : isError ? (
                <ErrorState
                    message={error instanceof Error ? error.message : 'Chargement impossible.'}
                    onRetry={() => void refetch()}
                />
            ) : announcements.length === 0 ? (
                <EmptyState
                    title="Aucune actualité"
                    description="Le panneau de l’accueil reste vide tant qu’aucun message n’est publié."
                />
            ) : (
                <div className="card overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                <th scope="col"></th>
                                <th scope="col">Titre</th>
                                <th scope="col">Flux</th>
                                <th scope="col">Publié le</th>
                                <th scope="col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {announcements.map((announcement) => (
                                <tr key={announcement.id}>
                                    <td className="w-8">
                                        {announcement.kind === 'rss' ? (
                                            <Rss
                                                className="w-4 h-4"
                                                style={{ color: announcement.feed?.color }}
                                                aria-label="Flux RSS"
                                            />
                                        ) : (
                                            <Newspaper
                                                className="w-4 h-4 text-[var(--color-text-muted)]"
                                                aria-label="Message manuel"
                                            />
                                        )}
                                    </td>
                                    <td>
                                        {announcement.url ? (
                                            <a
                                                href={announcement.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm font-medium text-[var(--color-text)] hover:text-[var(--color-primary)]"
                                            >
                                                {announcement.title}
                                            </a>
                                        ) : (
                                            <span className="text-sm font-medium text-[var(--color-text)]">
                                                {announcement.title}
                                            </span>
                                        )}
                                        {announcement.body && (
                                            <p className="text-xs text-[var(--color-text-muted)] mt-0.5 max-w-xl truncate">
                                                {announcement.body}
                                            </p>
                                        )}
                                    </td>
                                    <td className="text-xs">
                                        {announcement.feed && (
                                            <span
                                                className="px-2 py-0.5 rounded-full font-medium"
                                                style={{
                                                    backgroundColor: `${announcement.feed.color}1a`,
                                                    color: announcement.feed.color,
                                                }}
                                            >
                                                {announcement.feed.name}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                                        {formatDateTime(announcement.publishedAt)}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            type="button"
                                            onClick={() => archive.mutate(announcement.id)}
                                            disabled={archive.isPending}
                                            className="btn btn-ghost p-1.5 text-[var(--color-error)]"
                                            aria-label="Retirer"
                                            title="Retirer de l’accueil"
                                        >
                                            <Trash2 className="w-4 h-4" aria-hidden />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
